import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { initiateMediaUpload, updateMediaUploadProgress, uploadMedia, searchMedia, deleteMedia, invalidateUploadError, fetchingExternalLibrary } from "actions/media";

import _ from "lodash";
import cx from "classnames";

import { Modal, Col, Row, Button, Nav, ProgressBar, Tab, InputGroup, Form, Dropdown } from "react-bootstrap";
import { FileUploadButton } from "components/Buttons";
import { IKImage } from 'imagekitio-react';
import InfiniteScroll from "components/InfiniteScroll";
import { PaletteContext } from "components/Palette";
import Truncate from "react-truncate";
import { ExternalMediaView } from "./ExternalMedia";

import styles from "./library.module.scss";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export function MediaLibraryModal({ show, onChange, multiple, storyboard, target, mediaTypes = ['image', 'video'], onSelect, onClose, editorSession }) {
  const paletteContext = React.useContext(PaletteContext);

  const dispatch = useDispatch();
  const [selected, setSelected] = useState([]);
  const [uploadInstances, setUploadInstances] = useState([]);
  const [mediaType, setMediaType] = useState("image");
  const [mediaLibraryType, setMediaLibraryType] = useState("pexels");
  const [activeState, setActiveState] = useState("manageMedia");
  const [orientation, setOrientation] = useState("");
  const uploadError = useSelector(state => _.get(state, 'media.uploadError'));

  useEffect(() => {
    if (!show) {
      setSelected([]);
    }
  }, [show]);

  const handleClick = (media) => {
    setSelected((selected) => {
      var updated = [...selected];

      if (multiple) {
        const exists = updated.find(m => m.id === media.id) != null;
        if (exists) {
          updated = updated.filter(m => m.id !== media.id);
        } else {
          updated = [...updated, media];
        }
      } else {
        updated = [media];
      }

      return updated;
    });
  }

  const handleClose = () => {
    paletteContext.close();

    if (onClose) {
      onClose();
    }
    if (uploadError) {
      dispatch(invalidateUploadError());
    }
  }

  const handleUploadInstanceChange = (instance) => {
    setUploadInstances((state) => {
      return state.map(item => {
        if (item.files[0].id === instance.files[0].id) {
          return instance
        }

        return item;
      })
    });
  }

  const handleManageMediaClick = () => {
    setActiveState("manageMedia");
  }

  const handleExternalMedia = () => {
    setActiveState("pexels");
  }

  return (
    <Modal show={show} className={cx(styles.confirmation, styles.dialog)} backdropClassName={styles.overlay} centered>
      <Modal.Header>
        <div className={styles.mediaLibraryHeader}>
          Media Library
        </div>
        {onClose &&
          <div className={styles.closeButton}>
            <i className={cx("cleverstory-close")} onClick={handleClose} />
          </div>
        }
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <Tab.Pane>
          <Tab.Container id="left-tabs-example" defaultActiveKey="manageMedia">
            <div className={styles.mediaLibrary}>
              <div className={styles.mediaTypes}>
                <Nav variant="pills" >
                  <MediaUploadButton
                    storyboard={storyboard}
                    target={target}
                    mediaTypes={mediaTypes}
                    onInstanceChange={handleUploadInstanceChange} />
                  <Nav.Item>
                    <Nav.Link eventKey="manageMedia" className={styles.tabLinks} onClick={handleManageMediaClick}>
                      <i className={cx("cleverstory-background", styles.icon)} />My Uploads</Nav.Link>
                  </Nav.Item>
                  <hr />
                  <p className={styles.mediaTypeName}>Stock Media</p>
                  <Nav.Item>
                    <Nav.Link eventKey="pexels" className={styles.tabLinks} onClick={handleExternalMedia} >
                      <i className={cx("cleverstory-pexels", styles.icon)} />Pexels</Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <div className={styles.mediaList}>
                <Tab.Content>
                  <Tab.Pane eventKey="manageMedia">
                    <MediaGalleryView
                      mediaTypes={mediaTypes}
                      storyboard={storyboard}
                      target={target}
                      onSelect={handleClick}
                      selected={selected}
                      editable={true}
                      uploadInstances={uploadInstances}
                      onInstanceChange={handleUploadInstanceChange}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="pexels">
                    <ExternalMediaView
                      mediaType={mediaType}
                      onSelect={onSelect}
                      active={activeState}
                      orientation={orientation}
                      mediaLibraryType={mediaLibraryType}
                      onClose={onClose}
                      onChange={onChange}
                      editorSession={editorSession}
                      target={target}
                      editable={true}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </div>
          </Tab.Container>
        </Tab.Pane>
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm"
          variant="primary"
          disabled={_.size(selected) === 0}
          onClick={() => {
            onSelect(selected);
          }} >
          Choose Media
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const MediaUploadButton = ({ storyboard, target, mediaTypes, className }) => {

  const dispatch = useDispatch();
  const uploadError = useSelector(state => _.get(state, 'media.uploadError'));

  const handleUploadStart = (instances) => {
    instances.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateMediaUpload(upload));
    });
  }

  const handleUploadProgress = (file, progress) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleUploadComplete = (info, file) => {
    if (info) {
      var request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: _.get(file, 'dimension.width'),
        height: _.get(file, 'dimension.height'),
        storyboard: _.get(storyboard, 'id'),
        target: target,
      }

      if (file.type === 'video/mp4') {
        var video = document.createElement('video');

        video.onloadedmetadata = () => {
          request = {
            ...request,
            width: video.videoWidth,
            height: video.videoHeight
          }

          handleUploadMedia(request, file);
        };
        video.onerror = () => {

        };
        video.src = URL.createObjectURL(file);
      } else {
        handleUploadMedia(request, file);
      }
    }
  }

  const handleUploadMedia = (request, file) => {
    dispatch(uploadMedia(file.id, request, (response) => {
    }));
  }

  return (
    <FileUploadButton
      className={cx(styles.upload, className)}
      name="medaFile"
      multiple={true}
      accept={mediaTypes.map(type => {
        switch (type) {
          case 'image':
            return "image/*"
          case 'video':
            return "video/mp4"
        }
      })}
      onStart={handleUploadStart}
      onProgress={handleUploadProgress}
      onComplete={handleUploadComplete}
    >
      <Button variant="primary"><i className="cleverstory-upload" />Upload Media</Button>
    </FileUploadButton>
  )
}

export const MediaGalleryView = ({ mediaTypes, storyboard, orientation, target, onSelect, selected, editable, onInstanceChange }) => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const uploads = useSelector(state => _.get(state, 'media.uploads')) || [];
  const mediaList = useSelector(state => _.get(state, 'media.items')) || [];
  const uploadList = useSelector(state => _.get(state, 'media.uploads')) || [];
  const loading = useSelector(state => _.get(state, 'media.loading'));
  const pagination = useSelector(state => _.get(state, 'media.pagination'));
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;
  const error = useSelector(state => _.get(state, 'media.error'));
  const uploadingFail = useSelector(state => _.get(state, 'media.uploadError', false));

  const [selectedFilters, setSelectedFilters] = useState({ sortType: "createdDate", sortOrder: "desc" });

  const filterTypeOption = [
    {
      label: "Name",
      value: "name",
    },
    {
      label: "Date",
      value: "createdDate",
    },
  ];

  const onFilterChange = (type, value) => {
    setSelectedFilters((state) => {
      return {
        ...state,
        [type]: value,
      };
    });
  };

  useEffect(() => {
    handleSearchMedia();
  }, [selectedFilters])

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);


  const handleSearchMedia = () => {
    const request = {
      storyboard: _.get(storyboard, 'id'),
      target: target,
      mediaTypes: mediaTypes,
      page: 0,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const loadMoreMedia = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    const request = {
      storyboard: storyboard.id,
      target: target,
      page: pagination.currentPage + 1,
      mediaTypes: mediaTypes,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const handleClick = (media) => {
    if (!editable) {
      return;
    }

    onSelect(media);
  }

  const handleErrorClose = () => {
    setShowError(false);
  }

  const MediaErrorMessage = ({ message, onClose }) => {
    return (
      <div className={styles.errorMessage}>
        {message}
        <Button variant="info" onClick={() => onClose()} className={styles.closeIcon}><i className="cleverstory-close" /></Button>
      </div>
    )
  }

  const MediaEmptyMessage = () => {
    return (
      <div className={styles.emptyMediaContainer} >
        <div className={styles.emptyMessage}>
          <img className={cx(styles.emptyMediaImage)} alt="emptyMedia_image" />
          <div className={styles.errorMessageContent}>
            <div className={styles.errorMessageTitle}>
              <h2 className={styles.heading}>Weather forecast</h2>
            </div>
            <div className={styles.errorMessageSubText}>
              <span>Clear skies and <br /> empty screens</span>
            </div>
          </div>
        </div>
        {uploadingFail &&
          <MediaErrorMessage message='The video size should be less than 15mb.' onClose={handleUploadErrorClose} />
        }
      </div>
    )
  }

  const handleUploadErrorClose = () => {
    dispatch(invalidateUploadError());
  }

  const activeDropdownLabel = _.find(filterTypeOption, (data)=>{
    return data.value == selectedFilters.sortType;
  })

  return (
    <div className={cx(styles.right)}>
      <div className={cx(styles.searchMediaHeader)}>
        <div className={styles.search}>
          <InputGroup className={styles.searchBox}>
            <Form.Control
              className={styles.searchInput}
              placeholder="Search in media"
              aria-label="Search in media"
              aria-describedby="<Media> search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchMedia();
                }
              }}
            />
            <InputGroup.Text
              className={styles.searchIcon}
              id="basic-addon1"
              onClick={handleSearchMedia}
            >
              <i className="cleverstory-search" />
            </InputGroup.Text>
          </InputGroup>
        </div>
        <div className={styles.sortMedia}>
          <label className={styles.filterLabel}>Sort by:</label>
          <label className={styles.filterLabel}>{ }
            {selectedFilters.sortOrder && <img onClick={() => onFilterChange("sortOrder", selectedFilters.sortOrder === "ASC" ? "DESC" : "ASC")} className={cx(styles.sortIcon, selectedFilters.sortOrder === "ASC" ? styles.ascending : styles.descending)} alt="sortIcon" src='/images/sortIcon.svg' />}
          </label>
          <div className={styles.sortbyLabel}>
            <Dropdown className={styles.storboardTypeDropdown}>
              <Dropdown.Toggle id="dropdown-custom-components">
                {activeDropdownLabel.label}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {filterTypeOption.map((option, index) => {
                  return (
                    <Dropdown.Item
                      active={option.value == selectedFilters.sortType}
                      onClick={() => onFilterChange("sortType", option.value)}
                      key={index}
                      eventKey={index}
                    >
                      {option.label}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
      <InfiniteScroll
        className={cx(styles.scrollable, { [styles.editableSroll]: editable })}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={() => {
          loadMoreMedia();
        }}
      >
        <div className={styles.grid}>
          {uploads.map((upload) => {
            return (
              <MediaUploadIndicator
                key={upload.id}
                media={upload}
                instance={upload.instance}
                editable={editable}
                onDimensionChange={(dimension) => {
                  upload.instance.files[0].dimension = dimension;
                  onInstanceChange(upload.instance);
                }}
              />
            )
          })}

          {mediaList.length > 0 && mediaList.map(media => {
            let active = null;

            if (editable) {
              active = selected.find(m => m.id === media.id) != null;
            }

            if (media.type === 'image') {
              return <Media key={media.id}
                media={media}
                selected={active}
                editable={editable}
                onClick={() => {
                  handleClick(media);
                }}
              />
            }

            if (media.type === 'video') {
              return <Video key={media.id}
                muted
                media={media}
                selected={active}
                editable={editable}
                onClick={() => {
                  handleClick(media);
                }}
              />
            }
          })}
          {mediaList.length == 0 && uploadList.length == 0 && 
            <MediaEmptyMessage />
          }
        </div>
      </InfiniteScroll>
      {showError && _.size(error) > 0 &&
        <MediaErrorMessage message='The media cannot be deleted as it exists in one or more pages.' onClose={handleErrorClose} />
      }
      {uploadingFail &&
        <MediaErrorMessage message='The video size should be less than 15mb.' onClose={handleUploadErrorClose} />
      }
    </div>
  )
}

const Media = ({ media, selected, onClick, onDelete, editable }) => {
  const dispatch = useDispatch();
  const [confirm, showConfirm] = useState(false);

  return (
    <div className={cx(styles.media, { [styles.selected]: selected })} onClick={onClick}>
      <div className={styles.icon}>
        <IKImage path={media.url}
          transformation={[{
            "width": "600"
          }]}
          loading="lazy" />
        <div className={styles.overlay}>
          <div className={styles.actions}>
            <button onClick={() => { showConfirm(true); }} className={styles.action}>
              <i className={cx(styles.trashIcon, { [styles.disabled]: confirm }, 'cleverstory-trash')} />
            </button>
          </div>
          {confirm &&
            <div className={styles.deleteMediaConfimation}>
              <div className={styles.deleteMedia}>
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  onClick={() => { showConfirm(false); }}>Cancel</Button>
                <Button variant="primary" className={styles.deleteButton} onClick={() => {
                  dispatch(deleteMedia(media.id));
                  showConfirm(false);
                }}>Confirm</Button>
              </div>
            </div>
          }
        </div>
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-background")} />
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  );
}

const Video = ({ media, selected, onClick, onDelete, editable }) => {
  const dispatch = useDispatch();
  const videoRef = React.useRef(null);

  const [confirm, showConfirm] = useState(false);

  useEffect(() => {
    if (selected) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [selected]);

  return (
    <div className={cx(styles.media, { [styles.selected]: selected })} onClick={onClick}>
      <div className={styles.icon}>
        <video name="media" ref={videoRef} className={styles.videoPlayer} autoPlay loop muted>
          <source src={`${IMAGE_CDN_ENDPOINT}/${media.url}`} />
        </video>
        <div className={styles.overlay}>
          <div className={styles.actions}>
            <button onClick={() => { showConfirm(true); }} className={styles.action}>
              <i className={cx(styles.trashIcon, { [styles.disabled]: confirm }, 'cleverstory-trash')} />
            </button>
          </div>
          {confirm &&
            <div className={styles.deleteMediaConfimation}>
              <div className={styles.deleteMedia}>
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  onClick={() => { showConfirm(false); }}>Cancel</Button>
                <Button variant="primary" className={styles.deleteButton} onClick={() => {
                  dispatch(deleteMedia(media.id));
                  showConfirm(false);
                }}>Confirm</Button>
              </div>
            </div>
          }
        </div>
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-video")} />
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  )
}

const MediaUploadIndicator = ({ media, instance, onDimensionChange, editable }) => {
  const imageRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const fileType = _.get(instance, 'files[0].type', 'image');
  const type = fileType === 'video/mp4' ? 'video' : 'image';

  const previewFile = (file) => {
    if (!imageRef.current) {
      return;
    }

    var reader = new FileReader();

    reader.onloadend = function () {
      if (imageRef.current) {
        imageRef.current.src = reader.result;
      }
    }

    if (file) {
      reader.readAsDataURL(file);
    } else {
      imageRef.current.src = "";
    }
  }


  useEffect(() => {

    if (instance) {
      const file = instance.files[0];

      if (type === 'image' && imageRef) {
        imageRef.current.onload = onImageLoad;
        previewFile(file);
      }

      if (type === 'video' && videoRef) {
        var video = videoRef.current;
        var sources = video.getElementsByTagName('source');
        sources[0].src = URL.createObjectURL(file);
      }
    }
  }, [imageRef, videoRef]);

  const onImageLoad = () => {
    const dimension = {
      width: imageRef.current.naturalWidth,
      height: imageRef.current.naturalHeight
    }

    if (onDimensionChange) {
      onDimensionChange(dimension);
    }
  }

  return (
    <div className={cx(styles.media, styles.uploading)}>
      <div className={styles.icon}>
        {type === 'image' &&
          <img ref={imageRef} />
        }
        {type === 'video' &&
          <video ref={videoRef} name="media" className={styles.videoPlayer} autoPlay loop muted >
            <source />
          </video>
        }
        <div className={styles.overlay}>
          <div className={styles.actions}>
            {!media.processing &&
              <ProgressBar variant="warning" now={media.progress} />
            }

            {media.processing &&
              <div className={styles.status}>Processing...</div>
            }
          </div>
        </div>
      </div>
      <div className={styles.name}>
        <span className={styles.label}>
          <Truncate lines={1}>{media.name}</Truncate>
        </span>
      </div>
    </div>
  );
}